'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { emitTrackInlineContactFormViewEvent } from '@/src/analytics/hotpads/events';
import { ContactFormSpoke } from '../ContactForm';
import type { ContactSubmissionContext } from '../ContactForm/types';
import { innerCard, tealFrame, wrapperRelative } from './inlineContactModule.styles';

// Interaction-only: renders after a successful contact submit; matches the
// dynamic import in useContactFormAction so the chunk stays out of the eager bundle.
const PostContactFlowModal = dynamic(() =>
  import('../ContactForm/PostContactFlowModal').then((mod) => mod.PostContactFlowModal),
);

type Props = {
  listing: PropertyDetailListing;
};

const InlineContactModuleForListing = ({ listing }: Props) => {
  const [dismissedAfterSuccess, setDismissedAfterSuccess] = useState(false);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);
  const impressionTargetRef = useRef<HTMLDivElement>(null);
  const impressionTrackedRef = useRef(false);

  useEffect(() => {
    const target = impressionTargetRef.current;
    if (impressionTrackedRef.current || !target || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (impressionTrackedRef.current || !entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      impressionTrackedRef.current = true;
      observer.disconnect();
      emitTrackInlineContactFormViewEvent({ listing });
    });

    observer.observe(target);

    return () => observer.disconnect();
  }, [listing]);

  if (!listing || listing.active === false || dismissedAfterSuccess) {
    return null;
  }

  return (
    <div ref={impressionTargetRef} className={wrapperRelative}>
      <div className={tealFrame}>
        <div className={innerCard}>
          <ContactFormSpoke
            listing={listing}
            mode="contact"
            onClose={() => setDismissedAfterSuccess(true)}
            onSubmitSuccess={setPostContactContext}
            idPrefix="halo-inline-contact"
            formMode="inline"
          />
        </div>
      </div>
      <PostContactFlowModal
        context={postContactContext}
        onDone={() => {
          setPostContactContext(null);
          setDismissedAfterSuccess(true);
        }}
      />
    </div>
  );
};

/**
 * Every piece of state below belongs to one listing, but the HDP module list keys by module
 * type, so this stays mounted while the listing changes underneath it. The key gives the
 * whole module a per-listing lifetime instead of scoping each field by hand.
 */
export const InlineContactModule = ({ listing }: Props) => (
  <InlineContactModuleForListing key={listing.aliasEncoded} listing={listing} />
);
